// Light / dark theme toggle for admin + agent shells.
//
// Mirrors the player's existing ThemeToggle (in `core/components/
// ThemeToggle.tsx`) but persists the preference under role-specific
// localStorage keys so an admin's choice doesn't bleed into the
// player tab on the same device.
//
//   localStorage key   role
//   theme.admin        admin
//   theme.agent        agent
//
// Toggle live in the avatar dropdown menu in the top bar. Default is
// dark — matches today's admin look + reduces glare on long admin
// sessions.

import { useEffect, useState } from 'react';
import Icon from '../../components/Icon';
import { useAdminAuth } from '../lib/adminAuth';

function storageKey(role: string | undefined) {
  return role === 'agent' ? 'theme.agent' : 'theme.admin';
}

function readSaved(role: string | undefined): 'light' | 'dark' {
  try {
    const v = localStorage.getItem(storageKey(role));
    if (v === 'light' || v === 'dark') return v;
  } catch { /* private mode */ }
  return 'dark';
}

function applyTheme(mode: 'light' | 'dark') {
  if (mode === 'dark') document.documentElement.classList.add('dark');
  else document.documentElement.classList.remove('dark');
}

export default function AdminThemeToggle({ compact = false }: { compact?: boolean }) {
  const { admin } = useAdminAuth();
  const [mode, setMode] = useState<'light' | 'dark'>(() => readSaved(admin?.role));

  // Apply on mount + whenever role changes (sign-in flips it).
  useEffect(() => {
    const m = readSaved(admin?.role);
    setMode(m);
    applyTheme(m);
  }, [admin?.role]);

  function toggle() {
    const next = mode === 'dark' ? 'light' : 'dark';
    setMode(next);
    applyTheme(next);
    try { localStorage.setItem(storageKey(admin?.role), next); } catch {}
  }

  if (compact) {
    return (
      <button
        onClick={toggle}
        title={mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        className="grid place-items-center w-9 h-9 rounded-full
                   bg-neutral-100 hover:bg-neutral-200
                   dark:bg-white/5 dark:hover:bg-white/10
                   text-neutral-700 dark:text-neutral-200
                   active:scale-95 transition shrink-0"
      >
        <Icon name={mode === 'dark' ? 'light_mode' : 'dark_mode'} size={18} filled />
      </button>
    );
  }

  return (
    <button
      onClick={toggle}
      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl
                 hover:bg-white/5 active:bg-white/10 transition text-left"
    >
      <span className="grid place-items-center w-9 h-9 rounded-xl bg-violet-500/10 text-violet-300">
        <Icon name={mode === 'dark' ? 'light_mode' : 'dark_mode'} size={18} filled />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold">
          {mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
        </span>
        <span className="block text-[11.5px] text-neutral-400 truncate">
          {mode === 'dark' ? 'Bright mode for daytime' : 'Dim mode for night'}
        </span>
      </span>
    </button>
  );
}
